import { DOCUMENT } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, HostListener, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, finalize, takeUntil } from 'rxjs';

import { StorageService } from 'ngx-webstorage-service';

import { ApiService, AppStorageService, LoadingSpinnerService, NavigationService, PhxLocalizationService } from '@common';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { ToastService } from '@common/services/toast.service';

import { AppLoaderData } from './models/app-loader-data.model';
import { NavigateMessage } from './models/navigate-message.model';
import { RouteUpdateMessage } from './models/route-update-message.model';
import { TitleUpdateMessage } from './models/title-update-message.model';

@Component({
  selector: 'app-loader',
  templateUrl: './app-loader.component.html',
  styleUrls: ['./app-loader.component.less'],
  standalone: true
})
export class AppLoaderComponent extends BaseComponentOnDestroy implements OnInit, OnDestroy {
  iframeSource?: SafeResourceUrl;
  @ViewChild('appIframe') appIframe?: ElementRef<HTMLIFrameElement>;
  private parentRoutePath?: string;

  private readonly navigationService = inject(NavigationService);
  private readonly storageService: StorageService = inject(AppStorageService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly toastService = inject(ToastService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly router = inject(Router);
  private readonly titleService = inject(Title);
  private isRefreshingToken = false;
  private readonly bodyCssClass = 'app-loader-active';

  ngOnDestroy(): void {
    this.navigationService.updateRemovePaddingMarginState(false);
    this.renderer.removeClass(this.document.body, this.bodyCssClass);
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    this.navigationService.disableBreadCrumbsForComponent(this.isDestroyed$);
    this.navigationService.updateRemovePaddingMarginState(true);
    this.renderer.addClass(this.document.body, this.bodyCssClass);
    
    // Subscribe to route data changes to handle navigation between different app codes
    this.activatedRoute.data
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(data => {
        if (data.appLoaderData) {
          this.loadApplication(data.appLoaderData);
        }
      });

    // Listen to router navigation events to notify iframe when route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.isDestroyed$)
      )
      .subscribe(() => {
        this.handleRouteNavigation();
      });
  }

  handleIframeLoad(hasLoaded: boolean) {
    if (!hasLoaded) {
      this.toastService.logError('Failed to load application');
    }
    this.loadingSpinnerService.hide();
  }

  private loadApplication(appLoaderData: AppLoaderData): void {
    const { uri, queryParams, subPath, parentRoutePath } = appLoaderData;
    this.parentRoutePath = parentRoutePath;

    if (!uri) {
      const code = this.activatedRoute.snapshot.params.code;
      this.toastService.logError(`Could not find application with code: ${code}!`);
      return;
    }

    this.loadingSpinnerService.show();

    // Build the full URL path
    let fullUrl = subPath ? this.combineRoutePaths(uri, subPath) : uri;

    // Combine all query parameters
    const allQueryParams = {
      culture: PhxLocalizationService.locale,
      ...queryParams
    };

    // Build query string
    const queryString = this.buildQueryString(allQueryParams);
    if (queryString) {
      fullUrl += `?${queryString}`;
    }

    this.iframeSource = this.sanitizer.bypassSecurityTrustResourceUrl(fullUrl);
  }

  @HostListener('window:message', ['$event'])
  private handleReceivedMessage(event: MessageEvent): void {
    if (!event.data?.action) {
      return;
    }

    switch (event.data.action) {
      case 'REQUEST_TOKEN':
        this.handleTokenRequest();
        break;
      case 'NAVIGATE':
        this.handleNavigationRequest(event.data);
        break;
      case 'ROUTE_UPDATE':
        this.handleRouteUpdate(event.data);
        break;
      case 'UPDATE_TITLE':
        this.handleTitleUpdate(event.data);
        break;
      default:
        break;
    }
  }

  @HostListener('window:popstate', ['$event'])
  private handlePopstate(_event: PopStateEvent): void {
    const currentPath = window.location.pathname;
    
    if (!this.parentRoutePath || !currentPath.startsWith(this.parentRoutePath)) {
      return;
    }

    if (!this.appIframe?.nativeElement?.contentWindow) {
      return;
    }

    const iframePath = this.extractIframeRouteFromUrl(currentPath);
    const fullPath = iframePath + window.location.search;

    this.sendNavigationToIframe(fullPath);
  }

  private extractIframeRouteFromUrl(fullPath: string): string {
    if (!this.parentRoutePath) {
      return '/';
    }

    if (fullPath === this.parentRoutePath) {
      return '/';
    }

    if (fullPath.startsWith(this.parentRoutePath + '/')) {
      const subPath = fullPath.substring(this.parentRoutePath.length);
      return subPath || '/';
    }

    return '/';
  }

  private sendNavigationToIframe(iframePath: string): void {
    if (!this.appIframe?.nativeElement?.contentWindow) {
      return;
    }

    const message = {
      action: 'NAVIGATE',
      path: iframePath
    };

    this.appIframe.nativeElement.contentWindow.postMessage(message, '*');
  }

  private handleRouteNavigation(): void {
    const currentPath = window.location.pathname;
    
    if (!this.parentRoutePath || !currentPath.startsWith(this.parentRoutePath)) {
      return;
    }

    if (!this.appIframe?.nativeElement?.contentWindow) {
      return;
    }

    const iframePath = this.extractIframeRouteFromUrl(currentPath);
    const fullPath = iframePath + window.location.search;
    
    this.sendNavigationToIframe(fullPath);
  }

  private handleTokenRequest(): void {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.apiService
        .refreshToken()
        .pipe(
          takeUntil(this.isDestroyed$),
          finalize(() => {
            this.isRefreshingToken = false;
          })
        )
        .subscribe(() => {
          const token = this.storageService.get('BearerToken');
          this.appIframe?.nativeElement.contentWindow?.postMessage({ action: 'SET_TOKEN', token }, '*');
        });
    }
  }

  private handleNavigationRequest(data: NavigateMessage): void {
    if (!data.route) {
      this.toastService.logError('Navigation request missing route parameter');
      return;
    }

    try {
      // Determine the route to navigate to
      let targetRoute = data.route;
      
      if (data.isInternalRoute && this.parentRoutePath) {
        // For internal routes, navigate relative to the parent route path
        targetRoute = this.combineRoutePaths(this.parentRoutePath, data.route);
      }

      if (data.openInNewTab) {
        // Build URL for new tab
        const baseUrl = window.location.origin;
        let fullUrl = `${baseUrl}${targetRoute}`;
        
        // Add query parameters if provided
        const queryString = this.buildQueryString(data.queryParams);
        if (queryString) {
          fullUrl += `?${queryString}`;
        }
        
        window.open(fullUrl, '_blank');
      } else {
        const navigationExtras: any = {};
        
        if (data.queryParams) {
          navigationExtras.queryParams = data.queryParams;
        }
        
        if (data.replaceUrl) {
          navigationExtras.replaceUrl = true;
        }

        this.router.navigate([targetRoute], navigationExtras).catch(error => {
          this.toastService.logError(`Navigation failed: ${error.message || 'Unknown error'}`);
        });
      }
    } catch (error) {
      this.toastService.logError(`Invalid navigation request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private handleRouteUpdate(data: RouteUpdateMessage): void {
    if (!data.path) {
      this.toastService.logError('URL update request missing path parameter');
      return;
    }

    try {
      if (!this.parentRoutePath) {
        this.toastService.logError('Could not determine parent route path for URL update');
        return;
      }

      // Build the full path from parent route path
      let fullPath = this.parentRoutePath;
      
      // Add the sub-path if provided
      if (data.path && data.path !== '/') {
        fullPath = this.combineRoutePaths(this.parentRoutePath, data.path);
      }

      // Add query parameters if provided
      const queryString = this.buildQueryString(data.queryParams);
      if (queryString) {
        fullPath += `?${queryString}`;
      }

      // Update the URL using History API without triggering navigation
      if (data.replaceUrl) {
        window.history.replaceState(null, '', fullPath);
      } else {
        window.history.pushState(null, '', fullPath);
      }
    } catch (error) {
      this.toastService.logError(`Route update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private handleTitleUpdate(data: TitleUpdateMessage): void {
    if (!data.title) {
      this.toastService.logError('Title update request missing title parameter');
      return;
    }

    try {
      // Update the page title using Angular's Title service
      this.titleService.setTitle(data.title);
    } catch (error) {
      this.toastService.logError(`Title update failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private buildQueryString(queryParams?: Record<string, string>): string {
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return '';
    }

    return Object.entries(queryParams)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');
  }

  /**
   * Combines two route paths ensuring proper slash handling.
   * Removes trailing slashes from base path and ensures sub path starts with a slash.
   */
  private combineRoutePaths(basePath: string, subPath: string): string {
    if (!basePath || !subPath) {
      return basePath || subPath || '';
    }

    // Join the paths with a separator
    const combinedPath = [basePath, subPath].join('/');
    
    // Remove duplicate slashes, but preserve protocol schemes (e.g., https://)
    return combinedPath.replace(/(?<!:)\/+/g, '/');
  }
}
